/**
 * Теги звонка: метки с крестиком и поле «+ тег» с подсказками из уже
 * поставленных. Клики не уходят в строку списка (там клик — карточка).
 * Спека `docs/specs/2026-09-30-call-tags-design.md`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";

import { addCallTag, loadCallTags, removeCallTag } from "@/lib/calls";

export function CallTags({
  id,
  tags,
  onFilter,
}: {
  id: number;
  tags: string[];
  onFilter?: ((tag: string) => void) | undefined;
}) {
  const [current, setCurrent] = useState(tags);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const client = useQueryClient();
  // Подсказки нужны, только когда поле открыто.
  const all = useQuery({
    queryKey: ["call-tags"],
    queryFn: loadCallTags,
    enabled: editing,
  });

  const run = async (job: Promise<string[]>) => {
    setError("");
    try {
      setCurrent(await job);
      client.invalidateQueries({ queryKey: ["call-tags"] });
    } catch (e) {
      setError(String((e as Error).message));
    }
  };
  const submit = () => {
    const name = text.trim();
    if (name) void run(addCallTag(id, name));
    setText("");
    setEditing(false);
  };
  const listId = `call-tags-${id}`;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-[11.5px]"
      onClick={(e) => e.stopPropagation()}
      data-annot={`теги звонка ${id}`}
    >
      {current.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-accent-text"
        >
          {onFilter ? (
            <button
              type="button"
              onClick={() => onFilter(t)}
              className="hover:underline"
              title="Показать звонки с этим тегом"
            >
              {t}
            </button>
          ) : (
            t
          )}
          <button
            type="button"
            onClick={() => void run(removeCallTag(id, t))}
            className="text-fg-faint hover:text-err"
            title="Снять тег"
          >
            <X size={11} aria-hidden />
            <span className="sr-only">Снять тег {t}</span>
          </button>
        </span>
      ))}
      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            autoFocus
            list={listId}
            value={text}
            maxLength={64}
            onChange={(e) => setText(e.target.value)}
            onBlur={submit}
            onKeyDown={(e) =>
              e.key === "Escape" && (setText(""), setEditing(false))
            }
            placeholder="тег и Enter"
            className="h-6 w-44 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[11.5px]"
          />
          <datalist id={listId}>
            {(all.data ?? [])
              .filter((t) => !current.includes(t.name))
              .map((t) => (
                <option key={t.name} value={t.name}>
                  {t.calls}
                </option>
              ))}
          </datalist>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-border px-2 py-0.5 text-fg-faint hover:border-fg-muted hover:text-fg"
          title="Поставить тег"
        >
          <Plus size={11} aria-hidden />
          тег
        </button>
      )}
      {error && <span className="text-err">{error}</span>}
    </div>
  );
}
